/**
 * BD DETAILS COLUMNS - FIXED VERSION
 * Equipment → Category cascading handled in form logic
 */

import { ColumnDefinition, COLUMN_PRESETS } from '../../../universal_table';
import icrMapping from '../cell_data/icrMapping.json';
import equipmentCategoryMapping from '../cell_data/dropdownOptions.json';
import prMapping from '../cell_data/prMapping.json';

// Extract equipment list (keys from dropdownOptions.json)
export const EQUIPMENT_LIST = Object.keys(equipmentCategoryMapping);

// Extract ICRs from mapping
export const ICR_LIST = Object.keys(icrMapping);

// Get current month's PR value
export const getCurrentMonthPR = (): string => {
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });
  return prMapping[currentMonth as keyof typeof prMapping] || '0%';
};

// Helper to get categories for selected equipment
export const getCategoriesForEquipment = (equipment: string): string[] => {
  return equipmentCategoryMapping[equipment as keyof typeof equipmentCategoryMapping] || [];
};

// Helper to get inverters for selected ICR
export const getInvertersForICR = (icr: string): string[] => {
  return icrMapping[icr as keyof typeof icrMapping] || [];
};

export const BD_PARAMETERS = [
  "Budget PR for the month",
  "Equipment",
  "BD Category",
  "ICR No",
  "Inverter No.",
  "SCB's /QS/UNIT",
  "BD From",
  "BD up  to",
  "BD Minutes",
  "BD Hours",
  "PA BD Hours",
  "A = DC Capacity of affected equipment (kW)",
  "Generation loss kWh",
  "BD Description",
  "Corrective Action Taken",
  "Current Status",
  "Effective loss of POA",
  "Scope",
  "Which Equipment is in off condition",
  "Cause (Error Code)"
];

export const BD_DETAILS_COLUMNS: ColumnDefinition[] = [
  {
    key: '_delete',
    label: '',
    type: 'action',
    sticky: true,
    minWidth: 40,
    editable: false
  },
  {
    key: 'date',
    label: 'Date',
    ...COLUMN_PRESETS.DATE,
    sticky: true,
    minWidth: 110
  },
  {
    key: 'Budget PR for the month',
    label: 'Budgeted PR',
    abbreviation: 'Budgeted PR',
    ...COLUMN_PRESETS.READONLY,
    minWidth: 90
    // Will be set dynamically in form with getCurrentMonthPR()
  },
  {
    key: 'Equipment',
    label: 'Equipment',
    type: 'select',
    width: 'auto',
    minWidth: 120,
    options: EQUIPMENT_LIST // ✅ All equipment types from JSON
  },
  {
    key: 'BD Category',
    label: 'BD Category',
    abbreviation: 'Category',
    type: 'select',
    width: 'auto',
    minWidth: 150,
    options: [] // ✅ Will be populated dynamically in form based on Equipment
  },
  {
    key: 'ICR No',
    label: 'ICR No',
    type: 'select',
    width: 'auto',
    minWidth: 80,
    options: ICR_LIST // ✅ From icrMapping.json
  },
  {
    key: 'Inverter No.',
    label: 'Inverter No.',
    abbreviation: 'Inverter',
    type: 'select',
    width: 'auto',
    minWidth: 90,
    options: [] // ✅ Will be populated dynamically in form based on ICR
  },
  {
    key: "SCB's /QS/UNIT",
    label: "SCB's /QS/UNIT",
    abbreviation: 'SCB/QS',
    type: 'text',
    width: 'auto',
    minWidth: 100
  },
  {
    key: 'BD From',
    label: 'BD From',
    ...COLUMN_PRESETS.TIME,
    minWidth: 85
  },
  {
    key: 'BD up  to',
    label: 'BD up to',
    abbreviation: 'BD To',
    ...COLUMN_PRESETS.TIME,
    minWidth: 85
  },
  {
    key: 'BD Minutes',
    label: 'BD',
    abbreviation: 'BD',
    ...COLUMN_PRESETS.CALCULATED,
    unitLabel: 'Minutes',
    calculateFn: (rowData) => {
      if (!rowData['BD From'] || !rowData['BD up  to']) return null;
      
      // Helper function to parse time string (handles both 24-hour and 12-hour with AM/PM)
      const parseTimeToMinutes = (timeStr: string): number | null => {
        if (!timeStr || typeof timeStr !== 'string' || timeStr.trim() === '') return null;
        try {
          const cleanTime = timeStr.trim().toUpperCase();
          const hasAMPM = cleanTime.includes('AM') || cleanTime.includes('PM');
          
          if (hasAMPM) {
            const isPM = cleanTime.includes('PM');
            const timePart = cleanTime.replace(/AM|PM/gi, '').trim();
            const [hours, minutes = '0'] = timePart.split(':');
            let h = parseInt(hours, 10);
            const m = parseInt(minutes, 10);
            
            if (isNaN(h) || isNaN(m)) return null;
            
            if (isPM && h !== 12) h += 12;
            if (!isPM && h === 12) h = 0;
            
            return h * 60 + m;
          } else {
            const [hours, minutes = '0'] = cleanTime.split(':');
            const h = parseInt(hours, 10);
            const m = parseInt(minutes, 10);
            
            if (isNaN(h) || isNaN(m)) return null;
            
            return h * 60 + m;
          }
        } catch {
          return null;
        }
      };
      
      try {
        const fromStr = rowData['BD From'].toString().trim();
        const toStr = rowData['BD up  to'].toString().trim();
        
        const fromMinutes = parseTimeToMinutes(fromStr);
        const toMinutes = parseTimeToMinutes(toStr);
        
        if (fromMinutes === null || toMinutes === null) return null;
        
        let diff = ((toMinutes - fromMinutes + 1440) % 1440);
        
        // Handle next day scenario (when to < from)
        if (diff === 0 && fromMinutes !== toMinutes) {
          diff = (1440 - fromMinutes) + toMinutes;
        }
        
        return diff > 0 ? diff.toFixed(0) : null;
      } catch {
        return null;
      }
    },
    dependencies: ['BD From', 'BD up  to']
  },
  {
    key: 'BD Hours',
    label: 'BD',
    ...COLUMN_PRESETS.CALCULATED,
    unitLabel: 'Hours',
    decimalPlaces: 1,
    calculateFn: (rowData) => {
      const minutes = parseFloat(rowData['BD Minutes']) || 0;
      return minutes / 60; // Return number, formatter will handle decimal places
    },
    dependencies: ['BD Minutes']
  },
  {
    key: 'PA BD Hours',
    label: 'PA BD',
    abbreviation: 'PA',
    ...COLUMN_PRESETS.CALCULATED,
    unitLabel: 'Hours',
    decimalPlaces: 1,
    calculateFn: (rowData) => rowData['BD Hours'],
    dependencies: ['BD Hours']
  },
  {
    key: 'A = DC Capacity of affected equipment (kW)',
    label: 'DC Capacity',
    abbreviation: 'DC Cap',
    ...COLUMN_PRESETS.NUMBER,
    unitLabel: 'kW',
    minWidth: 100,
    decimalPlaces: 1
  },
  {
    key: 'Generation loss kWh',
    label: 'Generation Loss',
    abbreviation: 'Generation Loss',
    ...COLUMN_PRESETS.CALCULATED,
    unitLabel: 'kWh',
    decimalPlaces: 1,
    calculateFn: (rowData) => {
      const c = parseFloat(rowData['C=A*B']) || 0;
      const prRaw = rowData['Budget PR for the month'];
      const pr = parseFloat(String(prRaw ?? '').replace('%', '').trim()) || 0;
      return c * pr / 100; // Return number, formatter will handle decimal places
    },
    dependencies: ['C=A*B', 'Budget PR for the month'],
    minWidth: 100
  },
  {
    key: 'BD Description',
    label: 'BD Description',
    abbreviation: 'Description',
    type: 'text',
    width: 'auto',
    minWidth: 200
  },
  {
    key: 'Corrective Action Taken',
    label: 'Corrective Action',
    abbreviation: 'Action Taken',
    type: 'text',
    width: 'auto',
    minWidth: 200
  },
  {
    key: 'Current Status',
    label: 'Status',
    type: 'select',
    width: 'auto',
    minWidth: 100,
    options: ['Open', 'In Progress', 'Resolved', 'Closed']
  },
  // {
  //   key: 'Effective loss of POA',
  //   label: 'Effective POA Loss',
  //   abbreviation: 'Eff POA Loss',
  //   type: 'text',
  //   width: 'auto',
  //   minWidth: 120
  // },
  // {
  //   key: 'Scope',
  //   label: 'Scope',
  //   type: 'select',
  //   width: 'auto',
  //   minWidth: 100,
  //   options: ['Plant Level', 'ICR Level', 'Inverter Level', 'Equipment Level']
  // },
  // {
  //   key: 'Which Equipment is in off condition',
  //   label: 'Equipment Off',
  //   abbreviation: 'Off Equip',
  //   type: 'text',
  //   width: 'auto',
  //   minWidth: 120
  // },
  // {
  //   key: 'Cause (Error Code)',
  //   label: 'Cause/Error Code',
  //   abbreviation: 'Cause',
  //   type: 'text',
  //   width: 'auto',
  //   minWidth: 150
  // }
];
